/**
 * BookingModal — Date picker → time slots → contact details → mock advance
 * payment → success screen. Mounted from ProductCard when product.kind=SERVICE.
 *
 * Flow:
 *   1. fetch slots for the selected day
 *   2. customer picks a slot, enters name/mobile/address
 *   3. POST /api/services/book → mock Razorpay captures the advance
 *   4. Success screen with booking ref and amount paid
 */
import { useEffect, useMemo, useState } from "react";
import { X, Calendar, Clock, CheckCircle2, Loader2, AlertCircle, MapPin, Plus, ChevronRight } from "lucide-react";
import { api, inr } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import AddressPickerModal from "./AddressPickerModal";

function nextDays(advanceDays) {
    const out = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i <= advanceDays; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        out.push(d);
    }
    return out;
}

function ymd(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function BookingModal({ service, onClose }) {
    const { user } = useAuth();
    const advanceDays = service?.service_config?.advance_days ?? 7;
    const days = useMemo(() => nextDays(advanceDays), [advanceDays]);
    // iter146 — AT_SHOP services (salon/garage/clinic-style — the
    // customer travels to the merchant) vs the previously-only-possible
    // AT_CUSTOMER (home cook/cleaning — the merchant travels to the
    // customer). Defaults to AT_CUSTOMER so every service that existed
    // before this field was added keeps behaving exactly as before.
    const locationType = service?.service_config?.location_type === "AT_SHOP" ? "AT_SHOP" : "AT_CUSTOMER";

    const [date, setDate] = useState(days[0]);
    const [slotsData, setSlotsData] = useState(null);
    const [picked, setPicked] = useState(null);
    const [form, setForm] = useState({
        customer_name: user?.first_name || "",
        customer_mobile: user?.mobile_number || "",
        notes: "",
    });
    // Address picker — same UX as the product checkout's cart flow:
    // load saved addresses; the customer's last-used (from the header
    // / cart) is auto-selected; they can switch or add a new one.
    // Only relevant for AT_CUSTOMER services.
    const [addrs, setAddrs] = useState([]);
    const [selectedAddrId, setSelectedAddrId] = useState(null);
    const [showAddrSheet, setShowAddrSheet] = useState(false);
    const [showAddrPicker, setShowAddrPicker] = useState(false);
    const [step, setStep] = useState("slots"); // slots | details | success
    const [confirmed, setConfirmed] = useState(null);
    const [busy, setBusy] = useState(false);
    // AT_SHOP services show the SHOP's own address instead of asking
    // the customer to pick one — fetched once, read-only, no picker.
    const [shopInfo, setShopInfo] = useState(null);

    useEffect(() => {
        if (locationType !== "AT_SHOP" || !service?.restaurant_id) return;
        let cancelled = false;
        api.get(`/restaurants/${service.restaurant_id}`)
            // GET /restaurants/{id} wraps the doc as { restaurant, categories, gst } — not flat.
            .then(({ data }) => { if (!cancelled) setShopInfo(data?.restaurant || null); })
            .catch(() => { if (!cancelled) setShopInfo(null); });
        return () => { cancelled = true; };
    }, [locationType, service?.restaurant_id]);

    // Pull the customer's saved addresses + auto-select last-used
    // (mirrors how the product cart picks the default delivery addr).
    // Skipped entirely for AT_SHOP — there's no customer address to pick.
    useEffect(() => {
        if (!user || locationType !== "AT_CUSTOMER") return;
        let cancelled = false;
        api.get("/addresses").then(({ data }) => {
            if (cancelled) return;
            const rows = data || [];
            setAddrs(rows);
            if (!selectedAddrId && rows.length) {
                const lastId = (typeof window !== "undefined"
                    && window.localStorage.getItem("bizil_last_addr_id")) || null;
                const last = rows.find((a) => a.id === lastId);
                setSelectedAddrId((last || rows[0]).id);
            }
        }).catch(() => {});
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const selectedAddress = useMemo(
        () => addrs.find((a) => a.id === selectedAddrId) || null,
        [addrs, selectedAddrId],
    );

    useEffect(() => {
        if (!service?.id) return;
        setPicked(null);
        setSlotsData(null);
        api.get(`/services/${service.id}/slots?date=${ymd(date)}`)
            .then(({ data }) => setSlotsData(data))
            .catch(() => setSlotsData({ slots: [] }));
    }, [service?.id, date]);

    const advancePct = slotsData?.advance_payment_pct ?? service?.service_config?.advance_payment_pct ?? 0;
    const advanceAmount = Math.round((service?.selling_price || 0) * advancePct / 100);

    const submit = async () => {
        if (!user) {
            toast.error("Please log in to book.");
            window.location.href = "/login";
            return;
        }
        if (!form.customer_name.trim() || !form.customer_mobile.trim()) {
            toast.error("Name and mobile are required.");
            return;
        }
        // Compose the service address payload. AT_SHOP bookings have no
        // customer-picked address — the "location" that matters there is
        // the SHOP's own address, so send that through instead of null.
        // Without this, ``Booking.address`` stayed null for every AT_SHOP
        // booking and the merchant/customer/admin map on the booking card
        // (``BookingLocationMap``) had no lat/lng to render, silently
        // showing nothing (reported as "maps not working" for AT_SHOP
        // bookings like salon/spa appointments).
        const addressPayload = locationType === "AT_SHOP"
            ? (shopInfo?.address ? {
                label: shopInfo.name || service?.restaurant_name || "Shop",
                line1: shopInfo.address.line1,
                line2: shopInfo.address.line2,
                city: shopInfo.address.city,
                state: shopInfo.address.state,
                pincode: shopInfo.address.pincode,
                lat: shopInfo.address.lat,
                lng: shopInfo.address.lng,
            } : null)
            : (selectedAddress ? {
                id: selectedAddress.id,
                label: selectedAddress.label,
                line1: selectedAddress.line1,
                line2: selectedAddress.line2,
                city: selectedAddress.city,
                state: selectedAddress.state,
                pincode: selectedAddress.pincode,
                lat: selectedAddress.lat,
                lng: selectedAddress.lng,
            } : null);
        // Remember last-used address so subsequent bookings default to it.
        if (selectedAddrId && typeof window !== "undefined") {
            window.localStorage.setItem("bizil_last_addr_id", selectedAddrId);
        }
        setBusy(true);
        try {
            const { data } = await api.post("/services/book", {
                product_id: service.id,
                scheduled_at: picked.scheduled_at,
                customer_name: form.customer_name.trim(),
                customer_mobile: form.customer_mobile.trim(),
                address: addressPayload,
                notes: form.notes.trim() || null,
            });
            setConfirmed(data);
            setStep("success");
            toast.success("Booking confirmed!");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not complete booking. Please try a different slot.");
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-card border border-border rounded-2xl w-full max-w-md md:max-w-lg max-h-[92vh] flex flex-col relative"
                data-testid="booking-modal"
            >
                {/* iter146 — was a `sticky top-0` header floating inside a
                    single `overflow-y-auto` card. `position: sticky`
                    inside a scrollable box that's ALSO a flex-aligned
                    (`items-end`/`items-center`), height-capped child of
                    another fixed/flex ancestor is a known WebKit
                    trouble spot — this codebase already hit it enough
                    to grow a dedicated, PROVEN pattern for it
                    (`.responsive-modal-card` / `-header` / `-body` in
                    index.css § global responsive primitives): a plain
                    flex-column card with a `shrink-0` header and a
                    `flex-1 overflow-y-auto` body. No sticky positioning
                    at all, so there's no containing-block ambiguity for
                    WebKit to get wrong — this is what produced the
                    stray blank gap above the title and the cut-off
                    date/slot row right under it (user-reported
                    screenshot). Switched to that exact pattern here. */}
                <div className="shrink-0 bg-card border-b border-border">
                    <div className="px-5 py-3 flex items-center justify-between">
                        <h2 className="font-bold text-base line-clamp-1">{service?.name}</h2>
                        <button data-testid="booking-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted"><X size={18} /></button>
                    </div>
                    <div className="px-5 pb-3">
                        <div className="flex items-baseline justify-between">
                            <div>
                                <div className="text-[11px] uppercase font-bold text-muted-foreground tracking-wider">Service price</div>
                                <div className="text-lg font-extrabold">{inr(service?.selling_price || 0)}</div>
                            </div>
                            <div className="text-right">
                                <div className="text-[11px] uppercase font-bold text-muted-foreground tracking-wider">Pay now ({advancePct}%)</div>
                                <div className="text-lg font-extrabold text-primary">{inr(advanceAmount)}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scrollable body — flex-1 fills whatever space the
                    shrink-0 header above didn't take, and is the ONLY
                    thing that scrolls. safe-bottom keeps the final
                    button clear of the home-indicator area. */}
                <div className="flex-1 overflow-y-auto safe-bottom" style={{ WebkitOverflowScrolling: "touch" }}>
                {step === "slots" && (
                    <div className="px-5 py-4">
                        <h3 className="text-sm font-bold inline-flex items-center gap-2 mb-3"><Calendar size={16} /> Pick a date</h3>
                        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="booking-dates">
                            {days.map((d) => {
                                const active = ymd(d) === ymd(date);
                                return (
                                    <button
                                        key={d.toISOString()}
                                        data-testid={`booking-date-${ymd(d)}`}
                                        onClick={() => setDate(d)}
                                        className={`flex-shrink-0 min-w-[64px] flex flex-col items-center px-3 py-2 rounded-xl border ${active ? "bg-foreground text-background border-foreground" : "border-border"}`}
                                    >
                                        <span className="text-[10px] uppercase font-bold opacity-70">{d.toLocaleDateString("en-IN", { weekday: "short" })}</span>
                                        <span className="text-lg font-bold leading-none">{d.getDate()}</span>
                                        <span className="text-[10px] opacity-70">{d.toLocaleDateString("en-IN", { month: "short" })}</span>
                                    </button>
                                );
                            })}
                        </div>

                        <h3 className="text-sm font-bold inline-flex items-center gap-2 mt-5 mb-3"><Clock size={16} /> Available slots</h3>
                        {!slotsData ? (
                            <div className="grid grid-cols-3 gap-2">
                                {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-10 skeleton rounded-lg" />)}
                            </div>
                        ) : slotsData.slots.length === 0 ? (
                            <div className="text-sm text-muted-foreground text-center py-6 inline-flex items-center gap-2 justify-center w-full"><AlertCircle size={16}/> No slots available on this day.</div>
                        ) : (
                            <div className="grid grid-cols-3 gap-2" data-testid="booking-slots">
                                {slotsData.slots.map((s) => {
                                    const active = picked?.scheduled_at === s.scheduled_at;
                                    return (
                                        <button
                                            key={s.scheduled_at}
                                            data-testid={`booking-slot-${s.label.replace(/[: ]/g, "")}`}
                                            disabled={!s.available}
                                            onClick={() => setPicked(s)}
                                            className={`px-3 py-2 rounded-lg text-sm font-semibold border ${active ? "bg-primary text-primary-foreground border-primary" : s.available ? "border-border hover:border-foreground" : "border-border text-muted-foreground line-through opacity-50 cursor-not-allowed"}`}
                                        >{s.label}</button>
                                    );
                                })}
                            </div>
                        )}

                        <button
                            data-testid="booking-continue"
                            disabled={!picked}
                            onClick={() => setStep("details")}
                            className="mt-5 w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold disabled:opacity-50"
                        >Continue</button>
                    </div>
                )}

                {step === "details" && (
                    <div className="px-5 py-4 space-y-3">
                        <div className="bg-muted/50 border border-border rounded-xl p-3 text-sm flex items-start gap-2">
                            <Calendar size={16} className="mt-0.5 text-primary" />
                            <div>
                                <div className="font-semibold">{date.toDateString()}</div>
                                <div className="text-xs text-muted-foreground">{picked?.label} · {service?.service_config?.duration_min || 60} min</div>
                            </div>
                        </div>
                        <label className="block">
                            <span className="text-xs font-semibold text-muted-foreground">Your name</span>
                            <input data-testid="booking-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="block w-full bg-muted rounded-lg px-3 py-2 text-sm mt-1" />
                        </label>
                        <label className="block">
                            <span className="text-xs font-semibold text-muted-foreground">Mobile</span>
                            <input data-testid="booking-mobile" value={form.customer_mobile} onChange={(e) => setForm({ ...form, customer_mobile: e.target.value })} inputMode="tel" className="block w-full bg-muted rounded-lg px-3 py-2 text-sm mt-1" />
                        </label>
                        {locationType === "AT_SHOP" ? (
                            // iter146 — AT_SHOP: the customer travels to
                            // the merchant, so there's nothing to pick —
                            // show the shop's own address as read-only
                            // context instead of a picker.
                            <div data-testid="booking-shop-address">
                                <span className="text-xs font-semibold text-muted-foreground">You&apos;ll visit</span>
                                <div className="mt-1 w-full bg-muted rounded-lg px-3 py-2.5 flex items-start gap-2">
                                    <MapPin size={14} className="text-primary mt-0.5 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold">{shopInfo?.name || service?.restaurant_name || "This shop"}</p>
                                        {shopInfo?.address ? (
                                            <p className="text-[11px] text-muted-foreground line-clamp-2">
                                                {shopInfo.address.line1}{shopInfo.address.line2 ? `, ${shopInfo.address.line2}` : ""} · {shopInfo.address.city} {shopInfo.address.pincode}
                                            </p>
                                        ) : (
                                            <p className="text-[11px] text-muted-foreground">Address will be shared after booking</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <span className="text-xs font-semibold text-muted-foreground">Service address</span>
                                <button
                                    type="button"
                                    data-testid="booking-addr-trigger"
                                    onClick={() => setShowAddrSheet(true)}
                                    className="mt-1 w-full text-left bg-muted rounded-lg px-3 py-2.5 flex items-start gap-2 hover:bg-muted/70 transition"
                                >
                                    <MapPin size={14} className="text-primary mt-0.5 flex-shrink-0" />
                                    {selectedAddress ? (
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold capitalize">{selectedAddress.label}</p>
                                            <p className="text-[11px] text-muted-foreground line-clamp-1">{selectedAddress.line1}{selectedAddress.line2 ? `, ${selectedAddress.line2}` : ""} · {selectedAddress.city} {selectedAddress.pincode}</p>
                                        </div>
                                    ) : (
                                        <div className="flex-1 text-xs text-muted-foreground">Tap to select / add address (optional)</div>
                                    )}
                                    <ChevronRight size={14} className="text-muted-foreground mt-1" />
                                </button>
                            </div>
                        )}
                        <label className="block">
                            <span className="text-xs font-semibold text-muted-foreground">Notes (optional)</span>
                            <textarea data-testid="booking-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="block w-full bg-muted rounded-lg px-3 py-2 text-sm mt-1" />
                        </label>

                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
                            <p>You&apos;ll pay <b>{inr(advanceAmount)}</b> now (mocked Razorpay) — the remaining {inr((service?.selling_price || 0) - advanceAmount)} is due at service.</p>
                        </div>

                        <div className="flex gap-2">
                            <button onClick={() => setStep("slots")} className="px-4 py-3 rounded-xl border border-border font-semibold text-sm">Back</button>
                            <button
                                data-testid="booking-pay"
                                disabled={busy}
                                onClick={submit}
                                className="flex-1 bg-primary text-primary-foreground rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                            >{busy ? <Loader2 size={16} className="animate-spin" /> : null} Pay {inr(advanceAmount)} & Book</button>
                        </div>
                    </div>
                )}

                {step === "success" && confirmed && (
                    <div className="px-5 py-6 text-center" data-testid="booking-success">
                        <CheckCircle2 size={56} className="mx-auto text-emerald-500" />
                        <h3 className="text-lg font-bold mt-3">Booking confirmed</h3>
                        <p className="text-sm text-muted-foreground mt-1">{confirmed.booking.product_name}</p>
                        <div className="mt-4 inline-block bg-muted rounded-xl px-4 py-3 text-left text-sm">
                            <div><span className="text-muted-foreground">Slot:</span> <b>{new Date(confirmed.booking.scheduled_at).toLocaleString()}</b></div>
                            <div><span className="text-muted-foreground">Paid now:</span> <b>{inr(confirmed.booking.advance_paid)}</b></div>
                            <div><span className="text-muted-foreground">Payment ref:</span> <code className="text-xs">{confirmed.payment?.ref || "—"}</code></div>
                        </div>
                        <div className="flex gap-2 mt-5">
                            <button onClick={onClose} className="flex-1 border border-border rounded-xl py-3 font-semibold text-sm">Close</button>
                            <a href="/bookings" data-testid="booking-view-all" className="flex-1 bg-primary text-primary-foreground rounded-xl py-3 font-bold text-sm">My Bookings</a>
                        </div>
                    </div>
                )}
                </div>

                {showAddrSheet && (
                    <div className="absolute inset-0 z-10 bg-black/40 flex items-end" onClick={() => setShowAddrSheet(false)}>
                        <div className="bg-card w-full rounded-t-3xl p-4" onClick={(e) => e.stopPropagation()}>
                            <p className="font-bold text-base mb-2">Select service address</p>
                            <div className="space-y-2 max-h-[50vh] overflow-y-auto" data-testid="booking-addr-list">
                                {addrs.map((a) => (
                                    <button
                                        key={a.id}
                                        data-testid={`booking-addr-pick-${a.id}`}
                                        onClick={() => { setSelectedAddrId(a.id); setShowAddrSheet(false); }}
                                        className={`w-full text-left flex items-start gap-3 p-3 rounded-xl border ${selectedAddrId === a.id ? "border-primary bg-primary/5" : "border-border"}`}
                                    >
                                        <MapPin size={16} className="mt-0.5 text-primary" />
                                        <div className="flex-1 min-w-0">
                                            <p className="font-bold text-sm capitalize">{a.label}</p>
                                            <p className="text-xs text-muted-foreground line-clamp-2">{a.line1}{a.line2 ? `, ${a.line2}` : ""} · {a.city} {a.pincode}</p>
                                        </div>
                                    </button>
                                ))}
                                {addrs.length === 0 && (
                                    <p className="text-sm text-muted-foreground text-center py-4">No saved addresses yet.</p>
                                )}
                            </div>
                            <button
                                data-testid="booking-addr-add-new"
                                onClick={() => { setShowAddrSheet(false); setShowAddrPicker(true); }}
                                className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-muted rounded-xl px-3 py-3 font-semibold text-sm"
                            ><Plus size={14} /> Add new address</button>
                        </div>
                    </div>
                )}
            </div>

            {showAddrPicker && (
                <AddressPickerModal
                    onClose={() => setShowAddrPicker(false)}
                    onSaved={async (saved) => {
                        setShowAddrPicker(false);
                        try {
                            const { data } = await api.get("/addresses");
                            setAddrs(data || []);
                            if (saved?.id) setSelectedAddrId(saved.id);
                        } catch { /* noop */ }
                    }}
                />
            )}
        </div>
    );
}
